import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Award, Users, Share2, TrendingUp, Shield, CheckCircle, XCircle, Eye, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import AdminVideoManager from '../components/AdminVideoManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [prizeForm, setPrizeForm] = useState({
    name: '',
    description: '',
    image_url: '',
    signups_required: 1
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  // ID Verification states
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [showIdModal, setShowIdModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [cleaningBots, setCleaningBots] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadPendingVerifications();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setDashboard(response.data);
      setLoading(false);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied. Admin only.');
        navigate('/discover');
      } else {
        toast.error('Failed to load dashboard');
      }
    }
  };

  const loadPendingVerifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-verifications`, { withCredentials: true });
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (e) {
      console.error('Failed to load pending verifications:', e);
    }
  };

  const cleanupBots = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will remove ALL bot accounts (@example.com) and duplicate accounts.\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to proceed?'
    );
    
    if (!confirmed) return;
    
    setCleaningBots(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-bots`, {}, { withCredentials: true });
      
      toast.success(
        `✅ Cleanup Complete!\n` +
        `🤖 Bots removed: ${response.data.bots_removed}\n` +
        `🗑️ Duplicates removed: ${response.data.duplicates_removed}\n` +
        `👥 Real users remaining: ${response.data.final_user_count}`
      );
      
      // Reload dashboard
      loadDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaningBots(false);
    }
  };

  const handleViewId = (user) => {
    setSelectedUser(user);
    setVerificationNotes('');
    setShowIdModal(true);
  };

  const handleVerifyUser = async (action) => {
    if (!selectedUser) return;

    try {
      await axios.post(`${API}/admin/verify-user`, {
        user_id: selectedUser.id,
        action, // "approve" or "reject"
        notes: verificationNotes
      }, { withCredentials: true });

      toast.success(`User ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowIdModal(false);
      setSelectedUser(null);
      setVerificationNotes('');
      await loadPendingVerifications();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const getIdDocumentUrl = (user) => {
    return `${API}/admin/id-document/${user.id}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type (jpg, png, gif)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or GIF image');
      return;
    }

    // Check file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image must be less than 20MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload-prize-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const imageUrl = response.data.url;
      setPrizeForm({ ...prizeForm, image_url: imageUrl });
      setImagePreview(`${BACKEND_URL}${imageUrl}`);
      toast.success('Image uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleCreatePrize = async () => {
    if (!prizeForm.image_url) {
      toast.error('Please upload an image');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/prizes`, prizeForm, { withCredentials: true });
      toast.success('Prize created!');
      setShowPrizeModal(false);
      setPrizeForm({ name: '', description: '', image_url: '', signups_required: 1 });
      setImagePreview(null);
      await loadDashboard();
    } catch (e) {
      toast.error('Failed to create prize');
    }
  };

  const handleUpdatePrize = async () => {
    try {
      await axios.put(`${API}/admin/prizes/${editingPrize.id}`, prizeForm, { withCredentials: true });
      toast.success('Prize updated!');
      setShowPrizeModal(false);
      setEditingPrize(null);
      setPrizeForm({ name: '', description: '', image_url: '', signups_required: 1 });
      setImagePreview(null);
      await loadDashboard();
    } catch (e) {
      toast.error('Failed to update prize');
    }
  };

  const handleDeletePrize = async (prizeId) => {
    if (!window.confirm('Delete this prize?')) return;
    
    try {
      await axios.delete(`${API}/admin/prizes/${prizeId}`, { withCredentials: true });
      toast.success('Prize deleted');
      await loadDashboard();
    } catch (e) {
      toast.error('Failed to delete prize');
    }
  };

  const openCreateModal = () => {
    setEditingPrize(null);
    setPrizeForm({ name: '', description: '', image_url: '', signups_required: 1 });
    setImagePreview(null);
    setShowPrizeModal(true);
  };

  const openEditModal = (prize) => {
    setEditingPrize(prize);
    setPrizeForm({
      name: prize.name,
      description: prize.description,
      image_url: prize.image_url,
      signups_required: prize.signups_required
    });
    // Set preview for existing image
    if (prize.image_url) {
      setImagePreview(`${BACKEND_URL}${prize.image_url}`);
    } else {
      setImagePreview(null);
    }
    setShowPrizeModal(true);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/discover')}
              variant="ghost"
              size="icon"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">{dashboard?.total_users || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-2xl font-bold">{dashboard?.total_referrals || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Prizes</p>
                <p className="text-2xl font-bold">{dashboard?.prizes?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Database Cleanup Section */}
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-red-800 mb-2">🤖 Database Cleanup</h2>
              <p className="text-sm text-red-600">Remove bot accounts and duplicate users from the database</p>
            </div>
            <Button
              onClick={cleanupBots}
              disabled={cleaningBots}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cleaningBots ? 'Cleaning...' : '🧹 Clean Database'}
            </Button>
          </div>
        </div>

        {/* Video Carousel Management */}
        <div className="mb-8">
          <AdminVideoManager />
        </div>

        {/* Prizes Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Prizes Management</h2>
            <Button
              onClick={openCreateModal}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Prize
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboard?.prizes?.map((prize) => (
              <div key={prize.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-400 transition-all">
                {prize.image_url && (
                  <img src={prize.image_url} alt={prize.name} className="w-full h-32 object-cover rounded-lg mb-3" />
                )}
                <h3 className="font-bold text-lg mb-2">{prize.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{prize.description}</p>
                <p className="text-xs text-purple-600 font-semibold mb-3">
                  Requires {prize.signups_required} sign-ups
                </p>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => openEditModal(prize)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDeletePrize(prize.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ID Verification Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">ID Verifications</h2>
              <p className="text-sm text-gray-600">Review and approve user ID documents</p>
            </div>
            {pendingVerifications.length > 0 && (
              <div className="ml-auto">
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                  {pendingVerifications.length} Pending
                </span>
              </div>
            )}
          </div>

          {pendingVerifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No pending ID verifications</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingVerifications.map((user) => (
                <div key={user.id} className="border-2 border-yellow-200 rounded-xl p-4 hover:border-yellow-400 transition-all bg-yellow-50">
                  <div className="flex items-center space-x-3 mb-3">
                    <img
                      src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}`}
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-bold">{user.name}</h3>
                      <p className="text-xs text-gray-600">{user.email}</p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 mb-1">ID Type</p>
                    <p className="text-sm font-semibold capitalize">
                      {user.id_document_type?.replace('_', ' ') || 'Not specified'}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleViewId(user)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Review ID
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Analytics */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold mb-6">User Analytics</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4">User</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-center py-3 px-4">Likes</th>
                  <th className="text-center py-3 px-4">Shares</th>
                  <th className="text-center py-3 px-4">Referrals</th>
                  <th className="text-center py-3 px-4">Rewards</th>
                  <th className="text-left py-3 px-4">Referral Code</th>
                </tr>
              </thead>
              <tbody>
                {dashboard?.user_analytics?.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">{user.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                    <td className="py-3 px-4 text-center">{user.likes_count || 0}</td>
                    <td className="py-3 px-4 text-center">{user.share_count || 0}</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">{user.referral_count || 0}</td>
                    <td className="py-3 px-4 text-center">{user.rewards?.length || 0}</td>
                    <td className="py-3 px-4 text-xs font-mono bg-gray-100 rounded px-2 py-1">
                      {user.referral_code}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Prize Modal */}
      <Dialog open={showPrizeModal} onOpenChange={setShowPrizeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPrize ? 'Edit Prize' : 'Create Prize'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Prize Name</Label>
              <Input
                value={prizeForm.name}
                onChange={(e) => setPrizeForm({ ...prizeForm, name: e.target.value })}
                placeholder="e.g., Private Jet Trip"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={prizeForm.description}
                onChange={(e) => setPrizeForm({ ...prizeForm, description: e.target.value })}
                placeholder="e.g., All expenses paid luxury trip"
              />
            </div>

            <div>
              <Label>Prize Image</Label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
                {uploadingImage && (
                  <p className="text-sm text-gray-500">Uploading...</p>
                )}
                {imagePreview && (
                  <div className="mt-2">
                    <img src={imagePreview} alt="Prize preview" className="w-full h-32 object-cover rounded-lg border-2 border-gray-200" />
                  </div>
                )}
                {!imagePreview && !uploadingImage && (
                  <p className="text-xs text-gray-500">JPG, PNG, or GIF (max 20MB)</p>
                )}
              </div>
            </div>

            <div>
              <Label>Sign-ups Required</Label>
              <Input
                type="number"
                value={prizeForm.signups_required}
                onChange={(e) => setPrizeForm({ ...prizeForm, signups_required: parseInt(e.target.value) })}
                min="1"
              />
            </div>

            <Button
              onClick={editingPrize ? handleUpdatePrize : handleCreatePrize}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white"
            >
              {editingPrize ? 'Update Prize' : 'Create Prize'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ID Verification Modal */}
      <Dialog open={showIdModal} onOpenChange={setShowIdModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review ID Verification</DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <img
                  src={selectedUser.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name)}`}
                  alt={selectedUser.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-bold text-lg">{selectedUser.name}</h3>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    ID Type: <span className="font-semibold capitalize">{selectedUser.id_document_type?.replace('_', ' ') || 'Not specified'}</span>
                  </p>
                </div>
              </div>

              {/* ID Document */}
              <div>
                <Label className="text-sm font-medium mb-2 block">ID Document</Label>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                  <img
                    src={getIdDocumentUrl(selectedUser)}
                    alt="ID Document"
                    className="w-full h-auto object-contain bg-gray-50"
                    onError={(e) => {
                      // If image fails to load (might be PDF), show a message
                      e.target.style.display = 'none';
                      const container = e.target.parentElement;
                      if (!container.querySelector('.pdf-message')) {
                        const msg = document.createElement('div');
                        msg.className = 'pdf-message p-8 text-center';
                        msg.innerHTML = '<p class="text-gray-600 mb-4">PDF document detected</p><a href="' + getIdDocumentUrl(selectedUser) + '" target="_blank" class="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Download & View PDF</a>';
                        container.appendChild(msg);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Notes (Optional)</Label>
                <textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add notes about this verification (required for rejection)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[80px]"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  onClick={() => handleVerifyUser('approve')}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleVerifyUser('reject')}
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  ⚠️ Please verify the ID document carefully. Approved users can participate in the dating contest.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
